// The single source of truth for which tools (screening / monitoring) the
// signed-in account has access to. The dashboard BODY composition, the header
// CTA, and the onboarding popup all derive from this.
//
// NOTE: the plan does NOT gate the sidebar — every nav item is always shown
// (Fakhrul's call, 2026-06-01: a monitoring plan always includes screening, so
// nav never hides). Only the dashboard surface changes per plan.
//
// BACKEND SWAP POINT (ADR-010): `getActivePlan()` returns a mock today. When
// the backend lands, replace its body to read the company plan off the
// `GET /info/` bootstrap — every consumer (this dashboard + future surfaces)
// updates automatically, no other file changes. Until then, the `?tools=`
// override drives deterministic QA via the /preview/dashboard index + Playwright.

export type Tool = "screening" | "monitoring";

export interface Plan {
  tools: Tool[];
  /** Human label, e.g. "Screening + Monitoring". */
  name: string;
}

// Canonical ordering so derived labels/layout are stable regardless of input order.
const TOOL_ORDER: Tool[] = ["screening", "monitoring"];

const TOOL_LABELS: Record<Tool, string> = {
  screening: "Screening",
  monitoring: "Monitoring",
};

function planName(tools: Tool[]): string {
  const ordered = TOOL_ORDER.filter((t) => tools.includes(t));
  return ordered.length ? ordered.map((t) => TOOL_LABELS[t]).join(" + ") : "No plan";
}

export function getActivePlan(enableMonitoring = true): Plan {
  const tools: Tool[] = ["screening", ...(enableMonitoring ? (["monitoring"] as Tool[]) : [])];
  return { tools, name: planName(tools) };
}

/**
 * Resolve the plan to render, honoring a dev/QA `?tools=` override when present
 * (e.g. `?tools=screening` or `?tools=monitoring`) so every plan state is
 * exercisable without a backend. Falls back to the real plan otherwise.
 */
export function resolvePlan(toolsParam: string | null, enableMonitoring = true): Plan {
  if (!toolsParam) return getActivePlan(enableMonitoring);
  const requested = new Set(
    toolsParam
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  );
  const tools = TOOL_ORDER.filter((t) => requested.has(t));
  if (tools.length === 0) return getActivePlan();
  return { tools, name: planName(tools) };
}
